import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent } from '../ui/card';
import { SuccessModal } from './SuccessModal';
import { apiService } from '../../services/api';
import { toast } from 'sonner';

// Location data constants
const locationData = {
  countries: ['USA', 'Canada'],
  states: {
    USA: ['Minnesota', 'Texas', 'California', 'New York', 'Florida', 'Illinois', 'Washington'],
    Canada: ['Ontario', 'Quebec']
  },
  cities: {
    'USA-Minnesota': ['Minneapolis', 'St. Paul'],
    'USA-Texas': ['Austin', 'Houston'],
    'USA-California': ['San Francisco', 'Los Angeles', 'San Diego'],
    'USA-New York': ['New York City', 'Buffalo'],
    'USA-Florida': ['Miami', 'Orlando'],
    'USA-Illinois': ['Chicago', 'Springfield'],
    'USA-Washington': ['Seattle', 'Spokane'],
    'Canada-Ontario': ['Toronto', 'Ottawa'],
    'Canada-Quebec': ['Montreal', 'Quebec City']
  }
};

interface DealershipProfileFormProps {
  dealerId?: number;
}

export function DealershipProfileForm({ dealerId }: DealershipProfileFormProps) {
  const [formData, setFormData] = useState({
    dealershipName: '',
    emailAddress: '',
    phoneNumber: '',
    contactPersonName: '',
    streetAddress: '',
    sellerType: '',
    country: '',
    stateProvince: '',
    city: '',
    zipCode: '',
    description: '',
    website: ''
  });

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Load dealer data on component mount
  useEffect(() => {
    if (dealerId) {
      loadDealerData();
    } else {
      setIsLoadingData(false);
    }
  }, [dealerId]);

  const loadDealerData = async () => {
    try {
      const response = await apiService.getDealer(dealerId!);
      if (response) {
        // Infer country from state when not stored (e.g. Quebec, Ontario => Canada)
        const state = response.state || '';
        const inferredCountry = state === 'Quebec' || state === 'Ontario' ? 'Canada' : (state ? 'USA' : 'USA');
        setFormData({
          dealershipName: response.name || '',
          emailAddress: response.email || '',
          phoneNumber: response.phone || '',
          contactPersonName: response.name || '',
          streetAddress: response.address || '',
          sellerType: 'dealership',
          country: inferredCountry,
          stateProvince: state,
          city: response.city || '',
          zipCode: response.zip_code || '',
          description: response.description || '',
          website: response.website || ''
        });
      }
    } catch (error) {
      console.error('Error loading dealer data:', error);
      toast.error('Failed to load dealer profile. You can still edit and save below.');
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!dealerId) {
      toast.error('Dealer ID not found');
      return;
    }

    setIsLoading(true);
    try {
      // Convert form data to API format
      const updateData = {
        name: formData.dealershipName,
        email: formData.emailAddress,
        phone: formData.phoneNumber,
        address: formData.streetAddress,
        city: formData.city,
        state: formData.stateProvince,
        zip_code: formData.zipCode,
        description: formData.description,
        website: formData.website
      };

      await apiService.updateDealer(dealerId, updateData);
      
      // Store dealership location info globally for use in add car form
      localStorage.setItem('dealershipLocation', JSON.stringify({
        country: formData.country,
        stateProvince: formData.stateProvince,
        city: formData.city
      }));

      toast.success('Profile updated successfully!');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error saving dealer profile:', error);
      toast.error('Failed to save profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingData) {
    return (
      <div className="pt-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto pb-8">
        <div className="text-center">
          <p>Loading dealer profile...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="pt-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto pb-8">
        <div className="mb-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Dealership Information</h1>
          <p className="text-gray-600">Manage your dealership profile and contact details</p>
        </div>

        <Card>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Dealership Name</label>
                <Input
                  value={formData.dealershipName}
                  onChange={(e) => handleInputChange('dealershipName', e.target.value)}
                  placeholder="Enter dealership name"
                  className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Email Address</label>
                <Input
                  type="email"
                  value={formData.emailAddress}
                  onChange={(e) => handleInputChange('emailAddress', e.target.value)}
                  placeholder="Enter email address"
                  className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Phone Number</label>
                <Input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  placeholder="Enter phone number"
                  className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Contact Person Name</label>
                <Input
                  value={formData.contactPersonName}
                  onChange={(e) => handleInputChange('contactPersonName', e.target.value)}
                  placeholder="Enter contact person name"
                  className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-semibold text-gray-700">Street Address</label>
                <Input
                  value={formData.streetAddress}
                  onChange={(e) => handleInputChange('streetAddress', e.target.value)}
                  placeholder="Enter street address"
                  className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Country</label>
                <Select value={formData.country} onValueChange={(value) => {
                  handleInputChange('country', value);
                  handleInputChange('stateProvince', '');
                  handleInputChange('city', '');
                }}>
                  <SelectTrigger className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {locationData.countries.map(country => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">
                  {formData.country === 'Canada' ? 'Province' : 'State'}
                </label>
                <Select 
                  value={formData.stateProvince} 
                  onValueChange={(value) => {
                    handleInputChange('stateProvince', value);
                    handleInputChange('city', '');
                  }}
                  disabled={!formData.country}
                >
                  <SelectTrigger className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]">
                    <SelectValue placeholder={formData.country === 'Canada' ? 'Select province' : 'Select state'} />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.country && locationData.states[formData.country as keyof typeof locationData.states]?.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">City</label>
                <Select 
                  value={formData.city} 
                  onValueChange={(value) => handleInputChange('city', value)}
                  disabled={!formData.country || !formData.stateProvince}
                >
                  <SelectTrigger className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]">
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.country && formData.stateProvince && 
                      locationData.cities[`${formData.country}-${formData.stateProvince}` as keyof typeof locationData.cities]?.map(city => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">ZIP/Postal Code</label>
                <Input
                  value={formData.zipCode}
                  onChange={(e) => handleInputChange('zipCode', e.target.value)}
                  placeholder="Enter ZIP/postal code"
                  className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Website</label>
                <Input
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  placeholder="Enter website URL"
                  className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-semibold text-gray-700">Description</label>
                <Input
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Enter dealership description"
                  className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Seller Type</label>
                <Select value={formData.sellerType} onValueChange={(value) => handleInputChange('sellerType', value)}>
                  <SelectTrigger className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]">
                    <SelectValue placeholder="Select seller type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dealership">Dealership</SelectItem>
                    <SelectItem value="individual">Individual Seller</SelectItem>
                    <SelectItem value="auction">Auction House</SelectItem>
                    <SelectItem value="rental">Rental Company</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-center mt-8">
              <Button
                onClick={handleSave}
                disabled={isLoading}
                className="bg-[#f91bdc] hover:bg-[#e016c7] text-white px-8 py-3 rounded-lg font-semibold text-lg"
              >
                {isLoading ? 'Saving...' : 'Save Information'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        type="profileSaved"
      />
    </>
  );
}